import { FC, useCallback, useEffect, useState } from "react"
import { toast } from "react-toastify"

import { useTranslation } from "react-i18next"

import { ModalConfirmAction } from "components/modals/ModalConfirmAction"

import { cmsSeoService } from "services/cms-seo/cms-seo.service"
import { CmsSitemapSummary } from "services/cms-seo/type"

import { useBrandSite } from "../BrandSiteContext"
import { ghostButtonClass, InfoBox, primaryButtonClass, SectionHeading, WarnBox } from "../CmsUi"
import { formatDateTime } from "../helpers/format"

/** Blocking every crawler on a live domain deindexes the brand, so it is confirmed separately. */
const BLOCKS_EVERYTHING = /^\s*disallow:\s*\/\s*$/im

export const SitemapRobotsTab: FC = () => {
  const { t } = useTranslation("translation", { useSuspense: false })

  const site = useBrandSite()
  const [sitemap, setSitemap] = useState<CmsSitemapSummary | null>(null)
  const [body, setBody] = useState("")
  const [savedBody, setSavedBody] = useState("")
  const [updatedAt, setUpdatedAt] = useState<string | null>(null)
  const [loading, setLoading] = useState(false)
  const [saving, setSaving] = useState(false)
  const [pendingBody, setPendingBody] = useState<string | null>(null)

  const load = useCallback(async () => {
    setLoading(true)
    const [summary, robots] = await Promise.all([cmsSeoService.getSitemap(), cmsSeoService.getRobots()])
    setSitemap(summary)
    setBody(robots?.body ?? "")
    setSavedBody(robots?.body ?? "")
    setUpdatedAt(robots?.updated_at ?? null)
    setLoading(false)
  }, [])

  useEffect(() => {
    load()
  }, [load])

  const persist = async (value: string) => {
    setSaving(true)
    const saved = await cmsSeoService.saveRobots(value)
    setSaving(false)
    if (saved) {
      setSavedBody(saved.body)
      setBody(saved.body)
      setUpdatedAt(saved.updated_at)
      toast.success(t("cmsSeo.robotsSaved", { defaultValue: "robots.txt saved" }) as string)
    }
  }

  const onSave = () => {
    if (BLOCKS_EVERYTHING.test(body)) {
      setPendingBody(body)
      return
    }
    persist(body)
  }

  if (loading) {
    return <p className="py-10 text-center text-sm text-black dark:text-white">{t("common.loading", { defaultValue: "Loading..." })}</p>
  }

  return (
    <div className="flex flex-col gap-5 p-4 sm:p-5">
      <InfoBox>
        <b className="text-black dark:text-white">{t("cmsSeo.sitemaps", { defaultValue: "Sitemaps:" })}</b>{" "}
        {t("cmsSeo.sitemapSchedule", {
          defaultValue: "regenerated on every publish. Each file is listed in robots.txt, so there is one place to keep current."
        })}{" "}
        {site.localePrefix === "as-needed" &&
          t("cmsSeo.sitemapRootLocale", {
            defaultValue:
              "{{language}} pages are listed without a language code and every other language with one; both shapes side by side is how search engines are told which page is which.",
            language: site.defaultLocale.toUpperCase()
          })}
        <div className="mt-2.5 flex flex-wrap gap-1.5">
          {(sitemap?.children ?? []).map((child) => (
            <a
              key={child.name}
              href={child.url}
              target="_blank"
              rel="noreferrer"
              className="rounded-md border border-black/[0.08] px-2 py-0.5 font-mono text-[11.5px] text-purple-500 dark:border-white/[0.12] dark:text-[#c9a8fb]"
            >
              {child.name} · {child.url_count.toLocaleString()}
            </a>
          ))}
          {sitemap?.robots_url && (
            <a
              href={sitemap.robots_url}
              target="_blank"
              rel="noreferrer"
              className="rounded-md border border-black/[0.08] px-2 py-0.5 font-mono text-[11.5px] text-purple-500 dark:border-white/[0.12] dark:text-[#c9a8fb]"
            >
              robots.txt
            </a>
          )}
        </div>
        {sitemap?.last_generated_at && (
          <div className="mt-2 text-[11.5px] text-gray-500 dark:text-gray-400">
            {t("cmsSeo.lastGenerated", { defaultValue: "Last generated" })}: {formatDateTime(sitemap.last_generated_at)}
          </div>
        )}
      </InfoBox>

      <SectionHeading>robots.txt</SectionHeading>

      <textarea
        className="min-h-[220px] w-full rounded-xl border border-black/[0.1] bg-black/[0.02] p-3.5 font-mono text-xs leading-relaxed text-black dark:border-white/[0.12] dark:bg-[#0c0c12] dark:text-[#c9c9dc]"
        value={body}
        onChange={(event) => setBody(event.target.value)}
        spellCheck={false}
      />

      <p className="text-[11.5px] text-gray-400 dark:text-gray-500">
        {t("cmsSeo.robotsHint", {
          defaultValue:
            "The Sitemap lines are appended automatically and cannot be removed. Saving Disallow: / on a live domain asks for confirmation."
        })}
        {updatedAt ? ` · ${t("cmsSeo.lastSaved", { defaultValue: "Last saved" })}: ${formatDateTime(updatedAt)}` : ""}
      </p>

      {BLOCKS_EVERYTHING.test(body) && (
        <WarnBox>
          {t("cmsSeo.robotsBlocksAll", {
            defaultValue: "This file contains Disallow: / — every crawler will be blocked from the whole site."
          })}
        </WarnBox>
      )}

      <div className="flex gap-3">
        <button type="button" className={ghostButtonClass} disabled={body === savedBody} onClick={() => setBody(savedBody)}>
          {t("cmsSeo.revert", { defaultValue: "Revert" })}
        </button>
        <button type="button" className={primaryButtonClass} disabled={saving || body === savedBody} onClick={onSave}>
          {t("common.save", { defaultValue: "Save" })}
        </button>
      </div>

      <ModalConfirmAction
        data={pendingBody}
        handler={() => setPendingBody(null)}
        text={
          t("cmsSeo.confirmBlockAll", {
            defaultValue: "Disallow: / blocks every search engine from the whole site. Save it anyway?"
          }) as string
        }
        actionText={t("common.save", { defaultValue: "Save" })}
        actionClassName="btn-error"
        actionHandler={(value: string) => persist(value)}
      />
    </div>
  )
}
